import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Bell, CheckCircle, Mail, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminWaitlistTab({ waitlist, trips, token, onRefresh }) {
  const [notifyingTrip, setNotifyingTrip] = useState(null);

  const handleNotifyWaitlist = async (tripSlug, tripTitle) => {
    if (!window.confirm(`Notify all waitlisted users for "${tripTitle}" that a spot is available?`)) return;
    
    setNotifyingTrip(tripSlug);
    try {
      const res = await axios.post(`${API}/admin/notify-waitlist/${tripSlug}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to notify waitlist');
    } finally {
      setNotifyingTrip(null);
    }
  };

  // Group waitlist by trip
  const waitlistByTrip = {};
  waitlist.forEach(w => {
    const slug = w.trip_slug;
    if (!waitlistByTrip[slug]) {
      waitlistByTrip[slug] = {
        tripTitle: w.trip_title || trips.find(t => t.slug === slug)?.title || slug,
        entries: []
      };
    }
    waitlistByTrip[slug].entries.push(w);
  });

  return (
    <Card data-testid="admin-waitlist-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Waitlist Management
        </CardTitle>
      </CardHeader>
      <CardContent>
        {Object.keys(waitlistByTrip).length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No waitlist entries</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(waitlistByTrip).map(([slug, data]) => (
              <div key={slug} className="border rounded-lg overflow-hidden">
                <div className="bg-muted/50 p-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{data.tripTitle}</h3>
                    <p className="text-sm text-muted-foreground">
                      {data.entries.length} people waiting
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleNotifyWaitlist(slug, data.tripTitle)}
                    disabled={notifyingTrip === slug}
                  >
                    {notifyingTrip === slug ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Notifying...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Notify All
                      </>
                    )}
                  </Button>
                </div>
                <div className="divide-y">
                  {data.entries.map((entry) => (
                    <div key={entry.id} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{entry.name}</p>
                        <p className="text-sm text-muted-foreground">{entry.email}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Joined: {new Date(entry.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        {entry.notified ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Notified
                          </Badge>
                        ) : (
                          <Badge variant="outline">Waiting</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
